import Stripe from 'stripe';
import { BaseIntegration } from '../../core/BaseIntegration';
import type {
  IntegrationConfig,
  IntegrationResult,
  IntegrationError,
  IntegrationParams,
} from '../../types';
import { registerIntegration } from '../../registry';
import type {
  AlmadarCheckoutSession,
  AlmadarCustomer,
  AlmadarPortalSession,
  AlmadarSubscription,
  AlmadarSubscriptionStatus,
  AlmadarTier,
  CancelSubscriptionInput,
  CreateCheckoutInput,
  CreateCustomerInput,
  CreatePortalInput,
  StripePriceMap,
  UpdateSubscriptionInput,
} from './types';

export type {
  AlmadarCheckoutSession,
  AlmadarCustomer,
  AlmadarPortalSession,
  AlmadarSubscription,
  AlmadarSubscriptionStatus,
  AlmadarTier,
  AlmadarInvoicePayment,
  AlmadarInvoiceFailure,
  AlmadarStripeEvent,
  AlmadarStripeEventOk,
  CancelSubscriptionInput,
  CreateCheckoutInput,
  CreateCustomerInput,
  CreatePortalInput,
  StripePriceMap,
  UpdateSubscriptionInput,
} from './types';

export { verifyAndParseStripeEvent } from './webhooks';
export type { VerifyAndParseInput } from './webhooks';

const STRIPE_API_VERSION: Stripe.LatestApiVersion = '2025-02-24.acacia';

function isoFromUnix(seconds: number): string {
  return new Date(seconds * 1000).toISOString();
}

function priceToTier(priceId: string, prices: StripePriceMap): AlmadarTier | null {
  if (priceId === prices.solo) return 'solo';
  if (priceId === prices.teams) return 'teams';
  return null;
}

function mapStatus(status: Stripe.Subscription.Status): AlmadarSubscriptionStatus {
  switch (status) {
    case 'active':
    case 'past_due':
    case 'canceled':
    case 'incomplete':
    case 'incomplete_expired':
    case 'trialing':
    case 'unpaid':
      return status;
    case 'paused':
      return 'unpaid';
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return 'incomplete';
    }
  }
}

function shapeSubscription(
  sub: Stripe.Subscription,
  prices: StripePriceMap,
): AlmadarSubscription {
  const firstItem = sub.items.data[0];
  const priceId = firstItem?.price.id ?? '';
  const quantity = firstItem?.quantity ?? 1;
  const customerId =
    typeof sub.customer === 'string' ? sub.customer : sub.customer.id;
  return {
    subscriptionId: sub.id,
    customerId,
    status: mapStatus(sub.status),
    priceId,
    quantity,
    currentPeriodStart: isoFromUnix(sub.current_period_start),
    currentPeriodEnd: isoFromUnix(sub.current_period_end),
    cancelAtPeriodEnd: sub.cancel_at_period_end,
    tier: priceToTier(priceId, prices),
  };
}

function shapeCustomer(customer: Stripe.Customer): AlmadarCustomer {
  return {
    customerId: customer.id,
    email: customer.email ?? null,
    almadarUid: customer.metadata?.almadarUid ?? null,
  };
}

/**
 * Stripe integration for payment processing and subscription billing.
 *
 * Two surfaces:
 *   - Legacy `execute(action, params)` switch for the original three
 *     PaymentIntent actions (kept for back-compat).
 *   - New typed methods (`createCustomer`, `createCheckoutSession`,
 *     `createSubscription`, etc.) that return canonical `Almadar*`
 *     types. Consumers MUST prefer these — `Stripe.*` types never leak.
 */
export class StripeIntegration extends BaseIntegration {
  private client: Stripe;
  private prices: StripePriceMap;

  constructor(config: IntegrationConfig) {
    super(config);

    const apiKey = config.env.STRIPE_SECRET_KEY;
    if (!apiKey) {
      throw new Error('STRIPE_SECRET_KEY not configured');
    }

    this.client = new Stripe(apiKey, {
      apiVersion: STRIPE_API_VERSION,
    });

    this.prices = {
      solo: config.env.STRIPE_PRICE_SOLO ?? '',
      teams: config.env.STRIPE_PRICE_TEAMS ?? '',
    };

    this.logger.info('Stripe integration initialized');
  }

  /** Provisioned Price IDs the integration was constructed with. */
  getPrices(): StripePriceMap {
    return this.prices;
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();
    const retries = 0;

    try {
      let data: unknown;

      switch (action) {
        case 'createPaymentIntent':
          data = await this.executeWithRetry(() =>
            this.createPaymentIntent(params),
          );
          break;
        case 'confirmPayment':
          data = await this.executeWithRetry(() => this.confirmPayment(params));
          break;
        case 'refund':
          data = await this.executeWithRetry(() => this.refund(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime, retries),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async createPaymentIntent(params: IntegrationParams) {
    const { amount, currency, metadata } = params;
    this.logger.debug('Creating payment intent', {
      amount: Number(amount),
      currency: String(currency ?? ''),
    });
    return await this.client.paymentIntents.create({
      amount: amount as number,
      currency: currency as string,
      metadata: metadata as Stripe.MetadataParam | undefined,
    });
  }

  private async confirmPayment(params: IntegrationParams) {
    const { paymentIntentId } = params;
    this.logger.debug('Confirming payment', {
      paymentIntentId: String(paymentIntentId ?? ''),
    });
    return await this.client.paymentIntents.confirm(paymentIntentId as string);
  }

  private async refund(params: IntegrationParams) {
    const { paymentIntentId, amount } = params;
    this.logger.debug('Creating refund', {
      paymentIntentId: String(paymentIntentId ?? ''),
      amount: Number(amount),
    });
    return await this.client.refunds.create({
      payment_intent: paymentIntentId as string,
      amount: amount as number | undefined,
    });
  }

  // ───────────────────────────────────────────────────────────────────
  // Typed action surface — canonical Almadar shapes only.
  // ───────────────────────────────────────────────────────────────────

  /** Look up an existing customer by Stripe ID. */
  async getCustomer(customerId: string): Promise<AlmadarCustomer | null> {
    this.logger.debug('Fetching customer', { customerId });
    const customer = await this.client.customers.retrieve(customerId);
    if (customer.deleted === true) return null;
    return shapeCustomer(customer);
  }

  /**
   * Create a Stripe Customer for the given Almadar user. `almadarUid` is
   * stored as Stripe metadata so webhook handlers can resolve back to
   * the right `users/{uid}` document.
   */
  async createCustomer(input: CreateCustomerInput): Promise<AlmadarCustomer> {
    this.logger.debug('Creating customer', { almadarUid: input.almadarUid });
    const customer = await this.client.customers.create({
      email: input.email,
      name: input.displayName ?? undefined,
      metadata: { almadarUid: input.almadarUid },
    });
    return shapeCustomer(customer);
  }

  /**
   * Create a Stripe-hosted Checkout Session for the given tier. Client
   * redirects the user to the returned `url`; on success Stripe fires
   * `customer.subscription.created`, which the apps/builder webhook
   * handler turns into a `users/{uid}.tier` write.
   */
  async createCheckoutSession(
    input: CreateCheckoutInput,
  ): Promise<AlmadarCheckoutSession> {
    const price = input.tier === 'solo' ? this.prices.solo : this.prices.teams;
    if (!price) {
      throw new Error(`STRIPE_PRICE_${input.tier.toUpperCase()} not configured`);
    }

    this.logger.debug('Creating Checkout session', {
      tier: input.tier,
      quantity: input.quantity,
      hasCustomer: input.customerId !== null,
    });

    const session = await this.client.checkout.sessions.create({
      mode: 'subscription',
      customer: input.customerId ?? undefined,
      line_items: [{ price, quantity: input.quantity }],
      success_url: input.successUrl,
      cancel_url: input.cancelUrl,
      subscription_data: {
        metadata: input.metadata,
      },
      automatic_tax: { enabled: true },
      allow_promotion_codes: true,
    });

    return {
      url: session.url ?? '',
      sessionId: session.id,
      customerId:
        typeof session.customer === 'string'
          ? session.customer
          : session.customer?.id ?? null,
    };
  }

  /** Create a Billing Portal session for self-service plan management. */
  async createBillingPortalSession(
    input: CreatePortalInput,
  ): Promise<AlmadarPortalSession> {
    this.logger.debug('Creating Portal session', { customerId: input.customerId });
    const session = await this.client.billingPortal.sessions.create({
      customer: input.customerId,
      return_url: input.returnUrl,
    });
    return {
      url: session.url,
      customerId: input.customerId,
    };
  }

  /** Fetch a subscription and shape it into the canonical form. */
  async getSubscription(subscriptionId: string): Promise<AlmadarSubscription> {
    this.logger.debug('Fetching subscription', { subscriptionId });
    const sub = await this.client.subscriptions.retrieve(subscriptionId);
    return shapeSubscription(sub, this.prices);
  }

  /**
   * Create a subscription directly (server-side, no Checkout). Used by
   * P13.3 Solo → Teams upgrade flow.
   */
  async createSubscription(input: {
    customerId: string;
    tier: 'solo' | 'teams';
    quantity: number;
    metadata: Record<string, string>;
  }): Promise<AlmadarSubscription> {
    const price = input.tier === 'solo' ? this.prices.solo : this.prices.teams;
    if (!price) {
      throw new Error(`STRIPE_PRICE_${input.tier.toUpperCase()} not configured`);
    }
    this.logger.debug('Creating subscription', {
      customerId: input.customerId,
      tier: input.tier,
      quantity: input.quantity,
    });
    const sub = await this.client.subscriptions.create({
      customer: input.customerId,
      items: [{ price, quantity: input.quantity }],
      metadata: input.metadata,
      proration_behavior: 'create_prorations',
      automatic_tax: { enabled: true },
    });
    return shapeSubscription(sub, this.prices);
  }

  /**
   * Update quantity or cancel-at-period-end. Used for Teams seat resize
   * and Solo → Teams transition.
   */
  async updateSubscription(
    input: UpdateSubscriptionInput,
  ): Promise<AlmadarSubscription> {
    this.logger.debug('Updating subscription', { subscriptionId: input.subscriptionId });
    const update: Stripe.SubscriptionUpdateParams = {
      proration_behavior: 'create_prorations',
    };
    if (typeof input.quantity === 'number') {
      const existing = await this.client.subscriptions.retrieve(input.subscriptionId);
      const itemId = existing.items.data[0]?.id;
      if (itemId !== undefined) {
        update.items = [{ id: itemId, quantity: input.quantity }];
      }
    }
    if (typeof input.cancelAtPeriodEnd === 'boolean') {
      update.cancel_at_period_end = input.cancelAtPeriodEnd;
    }
    const sub = await this.client.subscriptions.update(
      input.subscriptionId,
      update,
    );
    return shapeSubscription(sub, this.prices);
  }

  /**
   * Cancel a subscription. Defaults to `atPeriodEnd: true` so the user
   * keeps access until the current period ends.
   */
  async cancelSubscription(
    input: CancelSubscriptionInput,
  ): Promise<AlmadarSubscription> {
    this.logger.debug('Canceling subscription', {
      subscriptionId: input.subscriptionId,
      atPeriodEnd: input.atPeriodEnd,
    });
    if (input.atPeriodEnd) {
      const sub = await this.client.subscriptions.update(input.subscriptionId, {
        cancel_at_period_end: true,
      });
      return shapeSubscription(sub, this.prices);
    }
    const sub = await this.client.subscriptions.cancel(input.subscriptionId);
    return shapeSubscription(sub, this.prices);
  }
}

// Auto-register
registerIntegration('stripe', StripeIntegration);
